import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  fromApiDateTime,
  toApiDateTime,
  type DateRange,
  type TimeZoneSetting,
} from '@/services/targeting/targeting'
import { DateTimePicker } from '../DateTimePicker'
import { FieldLabel } from '../FieldLabel'

export interface DateTargetValue {
  dateRanges: DateRange[]
  timeZoneSetting?: TimeZoneSetting
}

export interface DateTargetEditorProps {
  value: DateTargetValue
  onChange: (value: DateTargetValue) => void
}

/** Delivers only inside the given date ranges — flighting within the flight. */
export function DateTargetEditor({ value, onChange }: DateTargetEditorProps) {
  const { t } = useTranslation()

  const setRange = (index: number, patch: Partial<DateRange>) =>
    onChange({
      ...value,
      dateRanges: value.dateRanges.map((range, i) =>
        i === index ? { ...range, ...patch } : range
      ),
    })

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <FieldLabel className="text-xs font-medium">{t('targeting.timeZoneLabel')}</FieldLabel>
        <Select
          value={value.timeZoneSetting ?? 'SYSTEM'}
          onValueChange={(v) => onChange({ ...value, timeZoneSetting: v as TimeZoneSetting })}
        >
          <SelectTrigger size="sm" className="w-44">
            <SelectValue>
              {t(`targeting.timeZone.${value.timeZoneSetting ?? 'SYSTEM'}`)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="SYSTEM">{t('targeting.timeZone.SYSTEM')}</SelectItem>
            <SelectItem value="USER">{t('targeting.timeZone.USER')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {value.dateRanges.length === 0 && (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          {t('targeting.date.empty')}
        </p>
      )}

      {value.dateRanges.map((range, index) => (
        <div key={index} className="flex flex-wrap items-center gap-2 rounded-md border p-3">
          <DateTimePicker
            className="w-56"
            value={fromApiDateTime(range.first)}
            onChange={(iso) => setRange(index, { first: toApiDateTime(iso) })}
            placeholder={t('targeting.date.from')}
          />
          <span className="text-xs text-muted-foreground">{t('targeting.date.to')}</span>
          <DateTimePicker
            className="w-56"
            value={fromApiDateTime(range.second)}
            onChange={(iso) => setRange(index, { second: toApiDateTime(iso) })}
            placeholder={t('targeting.date.until')}
            defaultTime="end"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('common.remove')}
            className="ml-auto"
            onClick={() =>
              onChange({ ...value, dateRanges: value.dateRanges.filter((_, i) => i !== index) })
            }
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        onClick={() =>
          onChange({ ...value, dateRanges: [...value.dateRanges, { first: '', second: '' }] })
        }
      >
        <Plus className="size-4" />
        {t('targeting.date.addRange')}
      </Button>
    </div>
  )
}
